/** @file Picks the top-most slot under a world point from RenderBuffers runs, in reverse draw order. */
import { RENDER_LAYOUT } from '@pose2d/geometry/renderBuffers';
import type { RenderBuffers } from '@pose2d/geometry/types';
import { drawPageAt } from './_internal/drawPageAt';
import { readNum } from './_internal/readNum';
import { triangleWeightsAt } from './_internal/triangleWeightsAt';

/** Optional texture alpha test so transparent pixels are not selectable. */
export interface SlotPickOptions {
  /** Alpha 0..1 of texture page `page` at the interpolated vertex (u, v); null = no alpha test. */
  alpha: ((page: number, u: number, v: number) => number) | null;
  /** Hits whose alpha is below this fall through to the slots underneath. */
  alphaMin: number;
}

/** The picked slot, its run index and the index position of the hit triangle's first index. */
export interface SlotHit {
  slot: number;
  run: number;
  triangle: number;
}

const W = RENDER_LAYOUT.VERTEX_WORDS;
const R = RENDER_LAYOUT.RUN_STRIDE;
const NO_ALPHA: SlotPickOptions = { alpha: null, alphaMin: 0 };

/** True when the run's tint alpha (byte 3 of the light word of its first vertex) is zero. */
function invisible(buffers: RenderBuffers, start: number): boolean {
  const light = readNum(buffers.vertexU32, readNum(buffers.indices, start) * W + 4);
  return light >>> 24 === 0;
}

/** True when the triangle at t passes the alpha test at the barycentric weights w. */
function opaqueAt(buffers: RenderBuffers, t: number, w: Float64Array, opts: SlotPickOptions): boolean {
  if (opts.alpha === null) return true;
  let u = 0;
  let v = 0;
  for (let k = 0; k < 3; k++) {
    const base = readNum(buffers.indices, t + k) * W;
    u += readNum(w, k) * readNum(buffers.vertexF32, base + 2);
    v += readNum(w, k) * readNum(buffers.vertexF32, base + 3);
  }
  return opts.alpha(drawPageAt(buffers, t), u, v) >= opts.alphaMin;
}

/** Index position of the top-most triangle of run r containing (x, y), or -1. */
function hitRun(buffers: RenderBuffers, r: number, x: number, y: number, opts: SlotPickOptions, w: Float64Array): number {
  const start = readNum(buffers.runs, r * R + 1);
  const count = readNum(buffers.runs, r * R + 2);
  if (count < 3 || invisible(buffers, start)) return -1;
  for (let t = start + count - 3; t >= start; t -= 3) {
    if (triangleWeightsAt(buffers, t, x, y, w) && opaqueAt(buffers, t, w, opts)) return t;
  }
  return -1;
}

/** Top-most slot at world (wx, wy); needs buffers built with splitRuns 'slot' (no runs = no hit). */
export function pickSlot(buffers: RenderBuffers, wx: number, wy: number, opts: SlotPickOptions = NO_ALPHA): SlotHit | null {
  const w = new Float64Array(3);
  for (let r = buffers.runCount - 1; r >= 0; r--) {
    const t = hitRun(buffers, r, wx, wy, opts, w);
    if (t >= 0) return { slot: readNum(buffers.runs, r * R), run: r, triangle: t };
  }
  return null;
}
